import { DateTime } from 'luxon';

import type { Receipt, ReceiptLineItem } from './receiptsTypes';

/** Shown when extraction captured no merchant and the receipt has no file name to fall back to. */
export const DEFAULT_RECEIPT_TITLE = 'Expense receipt details';

/** Info-banner copy telling the user the extracted fields on a receipt detail view cannot be edited. */
export const RECEIPT_DETAILS_READ_ONLY_MESSAGE = 'These details were extracted from the receipt and are read-only.';

export type ReceiptSummaryItem = {
  label: string;
  amount: number;
};

export const getReceiptTitle = (receipt: Receipt): string =>
  receipt.extraction?.merchant || receipt.name || DEFAULT_RECEIPT_TITLE;

/**
 * Extracted transaction date for the receipt detail views, date-only — extraction dates are often
 * date-only to begin with, and neither surface shows a time. Undefined when extraction captured no
 * date or captured one that will not parse.
 */
export const getReceiptDateLabel = (receipt: Receipt): string | undefined => {
  const transactionDate = receipt.extraction?.transactionDate
    ? DateTime.fromISO(receipt.extraction.transactionDate)
    : undefined;

  return transactionDate?.isValid ? transactionDate.toFormat('LLL d, yyyy') : undefined;
};

/**
 * Label for one extracted line item: the description, suffixed with the quantity when the receipt
 * captured more than one of the item. A receipt can omit either field, so both are optional.
 */
export const getReceiptLineItemLabel = (lineItem: ReceiptLineItem): string => {
  const quantitySuffix = lineItem.quantity !== undefined && lineItem.quantity !== 1 ? ` (${lineItem.quantity}x)` : '';

  return `${lineItem.description ?? ''}${quantitySuffix}`;
};

/**
 * Subtotal / tax / tip rows in display order, omitting whatever extraction did not capture. Kept out
 * of the line-item list because these are receipt-level totals, not purchased items.
 */
export const getReceiptSummaryItems = (receipt: Receipt): ReceiptSummaryItem[] => {
  const { extraction } = receipt;
  const summaryItems: ReceiptSummaryItem[] = [];

  if (extraction?.subtotal !== undefined) {
    summaryItems.push({ label: 'Subtotal', amount: extraction.subtotal });
  }
  if (extraction?.tax !== undefined) {
    summaryItems.push({ label: 'Tax', amount: extraction.tax });
  }
  if (extraction?.tip !== undefined) {
    summaryItems.push({ label: 'Tip', amount: extraction.tip });
  }

  return summaryItems;
};
